"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import {
  type Participant,
  suggestionToParticipant,
  TeamCard,
} from "./base-match-template";
import { ActivityDisplayPreview } from "./previews";
import { ThrowballScoreEditor } from "./score-editors/throwball-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type ThrowballTemplateData = {
  matchScore: { yourSets: number; opponentSets: number };
  sets?: Array<{ yourScore: number; opponentScore: number }>;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type ThrowballParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type ThrowballTemplateProps = {
  currentUser: ThrowballParticipant;
  onChange: (
    data: ThrowballTemplateData,
    participants: ThrowballParticipant[],
  ) => void;
  initialData?: Partial<ThrowballTemplateData>;
};

export function ThrowballTemplate({
  currentUser,
  onChange,
  initialData,
}: ThrowballTemplateProps) {
  const [participants, setParticipants] = useState<ThrowballParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type ThrowballScorePart = Pick<ThrowballTemplateData, "matchScore" | "sets">;
  const [scorePart, setScorePart] = useState<ThrowballScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourSets: 0, opponentSets: 0 },
    sets: initialData?.sets ?? [],
  }));

  // Flexible team sizes (allows subs)
  const maxPerTeam = 15;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: ThrowballTemplateData = {
      matchScore: scorePart.matchScore,
      sets: scorePart.sets ?? [],
      positions: teams,
      hasScore: true,
    };
    onChange(data, participants);
  }, [scorePart, teams, participants, onChange]);

  const yourSets = scorePart.matchScore.yourSets;
  const opponentSets = scorePart.matchScore.opponentSets;

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    if (teamCount >= maxPerTeam) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourWon = yourSets > opponentSets;
  const opponentWon = opponentSets > yourSets;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
        />
      </div>

      {!opponentTeam.length && (
        <div className="rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning">
          <span>Add at least one opponent to post this match</span>
        </div>
      )}

      <ThrowballScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) => setScorePart(data as unknown as ThrowballScorePart)}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type ThrowballDisplayProps = {
  data: ThrowballTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function ThrowballDisplay({
  data,
  participants,
}: ThrowballDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="throwball"
      data={data}
      participants={participants}
    />
  );
}
